import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { BaseService } from '../base/base.service';
import { SessionUserHashManagerService } from '../session-user-hash-manager/session-user-hash-manager.service';
import {
  EmailTemplateCatalogue, EmailTemplatePreview, EmailTemplateTestResult, EmailWording, EmailWordingPreviewResult,
  EmailWordingSaveOutcome, EmailWordingState
} from '../../_models/admin/email-template.models';

/**
 * Typed calls to api/EmailTemplates: the catalogue, a sample-data preview, and the test send to the
 * signed-in admin. The API is admin-only and returns 404 while the emailtemplates.catalogue switch is off.
 *
 * Follows the shared BaseService pattern. prepareOptions() hashes JSON.stringify(payload).toLowerCase()
 * into the RequestMatch header, so the object passed to it MUST be the exact request body. The GETs send
 * no body and use prepareOptions(null); the test POST sends and hashes an empty object. prepareOptions()
 * also sets observe:'response', so responses are unwrapped.
 */
@Injectable({ providedIn: 'root' })
export class EmailTemplateService extends BaseService {

  private apiUrl: string = environment.apiUrl;

  constructor(private http: HttpClient, protected hashManagerService: SessionUserHashManagerService) {
    super(hashManagerService);
    this.apiUrl += this.apiUrl.endsWith('/') ? '' : '/';
  }

  public getCatalogue(): Observable<EmailTemplateCatalogue> {
    return this.http.get(this.apiUrl + 'api/EmailTemplates', this.prepareOptions(null)).pipe(
      map((data: any) => this.unwrap(data) as EmailTemplateCatalogue),
      catchError(this.handleError('getCatalogue'))
    );
  }

  public preview(key: string, applicationId: number | null): Observable<EmailTemplatePreview> {
    return this.http.get(this.url(key, 'Preview', applicationId), this.prepareOptions(null)).pipe(
      map((data: any) => this.unwrap(data) as EmailTemplatePreview),
      catchError(this.handleError('preview'))
    );
  }

  /** Sends the sample to the signed-in admin. The request carries no recipient. */
  public sendTest(key: string, applicationId: number | null): Observable<EmailTemplateTestResult> {
    // An empty JSON object as the body, and the same object hashed: the request carries no data.
    const body = {};
    return this.http.post(this.url(key, 'Test', applicationId), body, this.prepareOptions(body)).pipe(
      map((data: any) => this.unwrap(data) as EmailTemplateTestResult),
      catchError(this.handleError('sendTest'))
    );
  }

  public getWording(key: string): Observable<EmailWordingState> {
    return this.http.get(this.url(key, 'Wording', null), this.prepareOptions(null)).pipe(
      map((data: any) => this.unwrap(data) as EmailWordingState),
      catchError(this.handleError('getWording'))
    );
  }

  /** Saves replacement wording. A refusal (400) comes back as its reasons, not as an error. */
  public saveWording(key: string, wording: EmailWording): Observable<EmailWordingSaveOutcome> {
    const body = { subject: wording.subject, body: wording.body };
    return this.http.put(this.url(key, 'Wording', null), body, this.prepareOptions(body)).pipe(
      map((data: any) => ({ state: this.unwrap(data) as EmailWordingState, errors: [] } as EmailWordingSaveOutcome)),
      catchError((error: HttpErrorResponse) => error.status === 400 && Array.isArray(error.error)
        ? of({ state: null, errors: error.error as string[] })
        : this.handleError('saveWording')(error))
    );
  }

  /** Deletes the saved wording, so the code wording applies again. */
  public resetWording(key: string): Observable<EmailWordingState> {
    return this.http.delete(this.url(key, 'Wording', null), this.prepareOptions(null)).pipe(
      map((data: any) => this.unwrap(data) as EmailWordingState),
      catchError(this.handleError('resetWording'))
    );
  }

  /**
   * Makes one history entry's wording current again (emailtemplates.history-restore). An entry that no longer
   * passes the save's checks (422) comes back as its reasons, not as an error.
   */
  public restoreWording(key: string, historyId: number): Observable<EmailWordingSaveOutcome> {
    // An empty JSON object as the body, and the same object hashed: the entry is in the URL.
    const body = {};
    return this.http.post(this.url(key, `Wording/Restore/${historyId}`, null), body, this.prepareOptions(body)).pipe(
      map((data: any) => ({ state: this.unwrap(data) as EmailWordingState, errors: [] } as EmailWordingSaveOutcome)),
      catchError((error: HttpErrorResponse) => error.status === 422 && Array.isArray(error.error)
        ? of({ state: null, errors: error.error as string[] })
        : this.handleError('restoreWording')(error))
    );
  }

  /** A draft rendered with sample data. Saves and sends nothing. */
  public previewDraft(key: string, applicationId: number | null, wording: EmailWording): Observable<EmailWordingPreviewResult> {
    const body = { subject: wording.subject, body: wording.body };
    return this.http.post(this.url(key, 'Wording/Preview', applicationId), body, this.prepareOptions(body)).pipe(
      map((data: any) => this.unwrap(data) as EmailWordingPreviewResult),
      catchError(this.handleError('previewDraft'))
    );
  }

  private url(key: string, action: string, applicationId: number | null): string {
    const brand = applicationId ? `?applicationId=${applicationId}` : '';
    return `${this.apiUrl}api/EmailTemplates/${encodeURIComponent(key)}/${action}${brand}`;
  }

  /** Shared body unwrap — prepareOptions() observes the full response, not just the body. */
  private unwrap(data: any): any {
    return data ? data.body : data;
  }
}
